// src/pages/trading-portal/TradeExecution/PostTradeProcessingPage.js
// Post-Trade Processing - 4-tab interface: Trade Confirmations, Position Reconciliation, TCA, Tax Lots

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Skeleton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Grid,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    SettlementStatus, SettlementStatusLabels, SettlementStatusColors,
    ReconStatus, ReconStatusLabels, ReconStatusColors,
    BreakType, BreakTypeLabels, BreakTypeColors,
    QualityGrade, QualityGradeLabels, QualityGradeColors,
    TaxLotStatus, TaxLotStatusLabels, TaxLotStatusColors,
    CostBasisMethod, CostBasisMethodLabels, CostBasisMethodColors,
    OrderSideLabels, OrderSideColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const headCellSx = {
    fontFamily,
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const PostTradeProcessingPage = () => {
    // ==================== PERMISSIONS ====================
    const [canView, setCanView] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Shared state
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // ==================== TAB 0: TRADE CONFIRMATIONS ====================
    const [confirmations, setConfirmations] = useState([]);
    const [confLoading, setConfLoading] = useState(true);
    const [confPage, setConfPage] = useState(0);
    const [confPerPage, setConfPerPage] = useState(10);
    const [confTotal, setConfTotal] = useState(0);
    const [confSymbolFilter, setConfSymbolFilter] = useState('');
    const [confSettlementFilter, setConfSettlementFilter] = useState('');
    const [confSlippageAlertFilter, setConfSlippageAlertFilter] = useState('');

    // ==================== TAB 1: POSITION RECONCILIATION ====================
    const [reconRecords, setReconRecords] = useState([]);
    const [reconLoading, setReconLoading] = useState(false);
    const [reconPage, setReconPage] = useState(0);
    const [reconPerPage, setReconPerPage] = useState(10);
    const [reconTotal, setReconTotal] = useState(0);
    const [reconStatusFilter, setReconStatusFilter] = useState('');
    const [reconDateFilter, setReconDateFilter] = useState('');
    const [runningRecon, setRunningRecon] = useState(false);

    // ==================== TAB 2: TRANSACTION COST ANALYSIS ====================
    const [tcaRecords, setTcaRecords] = useState([]);
    const [tcaLoading, setTcaLoading] = useState(false);
    const [tcaPage, setTcaPage] = useState(0);
    const [tcaPerPage, setTcaPerPage] = useState(10);
    const [tcaTotal, setTcaTotal] = useState(0);
    const [tcaGradeFilter, setTcaGradeFilter] = useState('');
    const [tcaSymbolFilter, setTcaSymbolFilter] = useState('');
    const [runningTcaId, setRunningTcaId] = useState(null);

    // ==================== TAB 3: TAX LOTS ====================
    const [taxLots, setTaxLots] = useState([]);
    const [taxLoading, setTaxLoading] = useState(false);
    const [taxPage, setTaxPage] = useState(0);
    const [taxPerPage, setTaxPerPage] = useState(10);
    const [taxTotal, setTaxTotal] = useState(0);
    const [taxStatusFilter, setTaxStatusFilter] = useState('');
    const [taxSymbolFilter, setTaxSymbolFilter] = useState('');
    const [taxMethodFilter, setTaxMethodFilter] = useState('');

    // ==================== DIALOGS ====================
    // Resolve Break Dialog
    const [resolveDialog, setResolveDialog] = useState(false);
    const [resolveTarget, setResolveTarget] = useState(null);
    const [resolveNotes, setResolveNotes] = useState('');

    // ==================== FORMAT HELPERS ====================
    const formatPrice = (price) => price != null ? `$${parseFloat(price).toFixed(2)}` : '-';
    const formatNumber = (num) => num != null ? parseFloat(num).toLocaleString() : '-';
    const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '-';
    const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
    const formatBps = (val) => val != null ? `${parseFloat(val).toFixed(2)} bps` : '-';

    // ==================== PERMISSION CHECK ====================
    useEffect(() => {
        const checkPerms = async () => {
            const [view, edit] = await Promise.all([
                hasPermission('tr-tee-post-trade', 'view'),
                hasPermission('tr-tee-post-trade', 'edit'),
            ]);
            setCanView(view);
            setCanEdit(edit);
        };
        checkPerms();
    }, []);

    // ==================== TAB 0: TRADE CONFIRMATIONS ====================
    const fetchConfirmations = useCallback(async () => {
        if (!canView) return;
        setConfLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: confPage + 1, per_page: confPerPage });
            if (confSymbolFilter) params.append('symbol', confSymbolFilter);
            if (confSettlementFilter) params.append('settlement_status', confSettlementFilter);
            if (confSlippageAlertFilter) params.append('slippage_alert', confSlippageAlertFilter);
            const res = await axiosInstance.get(`/api/trade-execution/confirmations?${params.toString()}`);
            if (res.data.success) {
                setConfirmations(res.data.data || []);
                setConfTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch trade confirmations');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch trade confirmations');
        } finally {
            setConfLoading(false);
        }
    }, [canView, confPage, confPerPage, confSymbolFilter, confSettlementFilter, confSlippageAlertFilter]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchConfirmations();
    }, [fetchConfirmations, canView, activeTab]);

    // ==================== TAB 1: POSITION RECONCILIATION ====================
    const fetchReconciliation = useCallback(async () => {
        if (!canView) return;
        setReconLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: reconPage + 1, per_page: reconPerPage });
            if (reconStatusFilter) params.append('recon_status', reconStatusFilter);
            if (reconDateFilter) params.append('recon_date', reconDateFilter);
            const res = await axiosInstance.get(`/api/trade-execution/reconciliation?${params.toString()}`);
            if (res.data.success) {
                setReconRecords(res.data.data || []);
                setReconTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch reconciliation data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch reconciliation data');
        } finally {
            setReconLoading(false);
        }
    }, [canView, reconPage, reconPerPage, reconStatusFilter, reconDateFilter]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchReconciliation();
    }, [fetchReconciliation, canView, activeTab]);

    const handleRunReconciliation = async () => {
        setRunningRecon(true);
        try {
            const res = await axiosInstance.post('/api/trade-execution/reconciliation/run');
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Reconciliation run completed successfully', severity: 'success' });
                fetchReconciliation();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Reconciliation run failed', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Reconciliation run failed', severity: 'error' });
        } finally {
            setRunningRecon(false);
        }
    };

    const handleOpenResolve = (record) => {
        setResolveTarget(record);
        setResolveNotes('');
        setResolveDialog(true);
    };

    const handleResolveBreak = async () => {
        if (!resolveTarget) return;
        setSubmitting(true);
        try {
            const res = await axiosInstance.put(`/api/trade-execution/reconciliation/${resolveTarget.id}/resolve`, {
                resolution_notes: resolveNotes,
            });
            if (res.data.success) {
                setResolveDialog(false);
                setResolveTarget(null);
                setResolveNotes('');
                setSnackbar({ open: true, message: 'Break resolved successfully', severity: 'success' });
                fetchReconciliation();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'Failed to resolve break', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to resolve break', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // ==================== TAB 2: TRANSACTION COST ANALYSIS ====================
    const fetchTCA = useCallback(async () => {
        if (!canView) return;
        setTcaLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: tcaPage + 1, per_page: tcaPerPage });
            if (tcaGradeFilter) params.append('quality_grade', tcaGradeFilter);
            if (tcaSymbolFilter) params.append('symbol', tcaSymbolFilter);
            const res = await axiosInstance.get(`/api/trade-execution/tca?${params.toString()}`);
            if (res.data.success) {
                setTcaRecords(res.data.data || []);
                setTcaTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch TCA data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch TCA data');
        } finally {
            setTcaLoading(false);
        }
    }, [canView, tcaPage, tcaPerPage, tcaGradeFilter, tcaSymbolFilter]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchTCA();
    }, [fetchTCA, canView, activeTab]);

    const handleRunTCA = async (tcaId) => {
        setRunningTcaId(tcaId);
        try {
            const res = await axiosInstance.post(`/api/trade-execution/tca/${tcaId}/run`);
            if (res.data.success) {
                setSnackbar({ open: true, message: 'TCA analysis completed', severity: 'success' });
                fetchTCA();
            } else {
                setSnackbar({ open: true, message: res.data.message || 'TCA analysis failed', severity: 'error' });
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || 'TCA analysis failed', severity: 'error' });
        } finally {
            setRunningTcaId(null);
        }
    };

    // ==================== TAB 3: TAX LOTS ====================
    const fetchTaxLots = useCallback(async () => {
        if (!canView) return;
        setTaxLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: taxPage + 1, per_page: taxPerPage });
            if (taxStatusFilter) params.append('lot_status', taxStatusFilter);
            if (taxSymbolFilter) params.append('symbol', taxSymbolFilter);
            if (taxMethodFilter) params.append('cost_basis_method', taxMethodFilter);
            const res = await axiosInstance.get(`/api/trade-execution/tax-lots?${params.toString()}`);
            if (res.data.success) {
                setTaxLots(res.data.data || []);
                setTaxTotal(res.data.pagination?.total || 0);
            } else {
                setError(res.data.message || 'Failed to fetch tax lots');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch tax lots');
        } finally {
            setTaxLoading(false);
        }
    }, [canView, taxPage, taxPerPage, taxStatusFilter, taxSymbolFilter, taxMethodFilter]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchTaxLots();
    }, [fetchTaxLots, canView, activeTab]);

    // ==================== CSV EXPORT HELPER ====================
    const exportToCSV = (data, filename) => {
        if (!data || data.length === 0) return;
        const headers = Object.keys(data[0]);
        const csvRows = [
            headers.join(','),
            ...data.map(row =>
                headers.map(h => {
                    const val = row[h] == null ? '' : String(row[h]);
                    // Escape values that contain commas, quotes, or newlines
                    return val.includes(',') || val.includes('"') || val.includes('\n')
                        ? `"${val.replace(/"/g, '""')}"`
                        : val;
                }).join(',')
            ),
        ];
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ==================== RENDER: LOADING SKELETON ====================
    const renderSkeleton = (rows = 5) => (
        <Box sx={{ p: 3 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />
            ))}
        </Box>
    );

    // ==================== SLIPPAGE HELPERS ====================
    const getSlippageColor = (slippage) => {
        if (slippage == null) return '#64748b';
        const val = parseFloat(slippage);
        if (val > 2) return '#ef4444';
        if (val > 0) return '#f59e0b';
        return '#22c55e';
    };

    const getQualityScoreColor = (score) => {
        if (score == null) return '#64748b';
        const val = parseFloat(score);
        if (val >= 80) return '#22c55e';
        if (val >= 60) return '#3b82f6';
        if (val >= 40) return '#f59e0b';
        return '#ef4444';
    };

    const getGainLossColor = (val) => {
        if (val == null) return '#64748b';
        const num = parseFloat(val);
        if (num > 0) return '#22c55e';
        if (num < 0) return '#ef4444';
        return '#64748b';
    };

    // ==================== RENDER: TAB 0 - TRADE CONFIRMATIONS ====================
    const renderConfirmations = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Trade Confirmations
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.FileDownload />}
                        onClick={() => exportToCSV(confirmations, 'trade_confirmations.csv')}
                        disabled={!confirmations.length}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchConfirmations}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search symbol..."
                    value={confSymbolFilter}
                    onChange={(e) => { setConfSymbolFilter(e.target.value); setConfPage(0); }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                    InputProps={{
                        startAdornment: <MuiIcons.Search sx={{ fontSize: 18, color: '#94a3b8', mr: 0.5 }} />,
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ fontFamily }}>Settlement Status</InputLabel>
                    <Select
                        value={confSettlementFilter}
                        onChange={(e) => { setConfSettlementFilter(e.target.value); setConfPage(0); }}
                        label="Settlement Status"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(SettlementStatus).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{SettlementStatusLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontFamily }}>Slippage Alert</InputLabel>
                    <Select
                        value={confSlippageAlertFilter}
                        onChange={(e) => { setConfSlippageAlertFilter(e.target.value); setConfPage(0); }}
                        label="Slippage Alert"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true" sx={{ fontFamily }}>Alert Only</MenuItem>
                        <MenuItem value="false" sx={{ fontFamily }}>No Alert</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {confLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Symbol</TableCell>
                                        <TableCell sx={headCellSx}>Side</TableCell>
                                        <TableCell sx={headCellSx}>Qty</TableCell>
                                        <TableCell sx={headCellSx}>Avg Fill</TableCell>
                                        <TableCell sx={headCellSx}>Expected Price</TableCell>
                                        <TableCell sx={headCellSx}>Slippage (bps)</TableCell>
                                        <TableCell sx={headCellSx}>Alert</TableCell>
                                        <TableCell sx={headCellSx}>Commission</TableCell>
                                        <TableCell sx={headCellSx}>Settlement</TableCell>
                                        <TableCell sx={headCellSx}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {confirmations.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.ReceiptLong sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No trade confirmations found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : confirmations.map((conf) => (
                                        <TableRow key={conf.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {conf.symbol || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={OrderSideLabels[conf.side] || conf.side}
                                                    size="small"
                                                    color={OrderSideColors[conf.side] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatNumber(conf.quantity)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatPrice(conf.avg_fill_price)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatPrice(conf.expected_price)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: getSlippageColor(conf.slippage_bps), fontSize: '0.82rem' }}>
                                                {conf.slippage_bps != null ? `${parseFloat(conf.slippage_bps).toFixed(2)}` : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {conf.slippage_alert ? (
                                                    <Tooltip title="Slippage exceeds threshold">
                                                        <MuiIcons.Warning sx={{ fontSize: 20, color: '#f59e0b' }} />
                                                    </Tooltip>
                                                ) : (
                                                    <MuiIcons.CheckCircleOutline sx={{ fontSize: 20, color: '#94a3b8' }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {formatPrice(conf.commission)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={SettlementStatusLabels[conf.settlement_status] || conf.settlement_status}
                                                    size="small"
                                                    color={SettlementStatusColors[conf.settlement_status] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                                {formatDateTime(conf.confirmation_date || conf.created_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={confTotal}
                            page={confPage}
                            onPageChange={(_, newPage) => setConfPage(newPage)}
                            rowsPerPage={confPerPage}
                            onRowsPerPageChange={(e) => { setConfPerPage(parseInt(e.target.value, 10)); setConfPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 1 - POSITION RECONCILIATION ====================
    const renderReconciliation = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Position Reconciliation
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.FileDownload />}
                        onClick={() => exportToCSV(reconRecords, 'position_reconciliation.csv')}
                        disabled={!reconRecords.length}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchReconciliation}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                    {canEdit && (
                        <Button
                            startIcon={runningRecon ? <MuiIcons.HourglassEmpty /> : <MuiIcons.PlayArrow />}
                            variant="contained"
                            onClick={handleRunReconciliation}
                            disabled={runningRecon}
                            size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                        >
                            {runningRecon ? 'Running...' : 'Run Reconciliation'}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ fontFamily }}>Recon Status</InputLabel>
                    <Select
                        value={reconStatusFilter}
                        onChange={(e) => { setReconStatusFilter(e.target.value); setReconPage(0); }}
                        label="Recon Status"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(ReconStatus).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{ReconStatusLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    size="small"
                    type="date"
                    label="Recon Date"
                    value={reconDateFilter}
                    onChange={(e) => { setReconDateFilter(e.target.value); setReconPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                />
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {reconLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Recon Date</TableCell>
                                        <TableCell sx={headCellSx}>Symbol</TableCell>
                                        <TableCell sx={headCellSx}>Internal Qty</TableCell>
                                        <TableCell sx={headCellSx}>Broker Qty</TableCell>
                                        <TableCell sx={headCellSx}>Break</TableCell>
                                        <TableCell sx={headCellSx}>Status</TableCell>
                                        <TableCell sx={headCellSx}>Break Type</TableCell>
                                        <TableCell sx={headCellSx}>Resolution Notes</TableCell>
                                        <TableCell sx={headCellSx}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reconRecords.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.CompareArrows sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No reconciliation records found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : reconRecords.map((rec) => {
                                        const breakQty = rec.break_quantity != null ? parseFloat(rec.break_quantity) : null;
                                        const hasBreak = breakQty != null && breakQty !== 0;
                                        return (
                                            <TableRow key={rec.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                                <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {formatDate(rec.recon_date)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {rec.symbol || '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {formatNumber(rec.internal_quantity)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {formatNumber(rec.broker_quantity)}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontFamily,
                                                    fontWeight: hasBreak ? 700 : 400,
                                                    color: hasBreak ? '#ef4444' : '#22c55e',
                                                    fontSize: '0.82rem',
                                                    bgcolor: hasBreak ? '#fef2f2' : 'transparent',
                                                }}>
                                                    {breakQty != null ? formatNumber(breakQty) : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={ReconStatusLabels[rec.recon_status] || rec.recon_status}
                                                        size="small"
                                                        color={ReconStatusColors[rec.recon_status] || 'default'}
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {rec.break_type ? (
                                                        <Chip
                                                            label={BreakTypeLabels[rec.break_type] || rec.break_type}
                                                            size="small"
                                                            color={BreakTypeColors[rec.break_type] || 'default'}
                                                            variant="outlined"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                        />
                                                    ) : (
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.82rem' }}>-</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#475569', fontSize: '0.78rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {rec.resolution_notes || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {canEdit && (rec.recon_status === 'BREAK_DETECTED' || rec.recon_status === 'INVESTIGATING') && (
                                                        <Tooltip title="Resolve Break">
                                                            <IconButton size="small" onClick={() => handleOpenResolve(rec)}>
                                                                <MuiIcons.BuildCircle sx={{ fontSize: 18, color: ACCENT }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={reconTotal}
                            page={reconPage}
                            onPageChange={(_, newPage) => setReconPage(newPage)}
                            rowsPerPage={reconPerPage}
                            onRowsPerPageChange={(e) => { setReconPerPage(parseInt(e.target.value, 10)); setReconPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 2 - TRANSACTION COST ANALYSIS ====================
    const renderTCA = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Transaction Cost Analysis (TCA)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.FileDownload />}
                        onClick={() => exportToCSV(tcaRecords, 'transaction_cost_analysis.csv')}
                        disabled={!tcaRecords.length}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchTCA}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search symbol..."
                    value={tcaSymbolFilter}
                    onChange={(e) => { setTcaSymbolFilter(e.target.value); setTcaPage(0); }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                    InputProps={{
                        startAdornment: <MuiIcons.Search sx={{ fontSize: 18, color: '#94a3b8', mr: 0.5 }} />,
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontFamily }}>Quality Grade</InputLabel>
                    <Select
                        value={tcaGradeFilter}
                        onChange={(e) => { setTcaGradeFilter(e.target.value); setTcaPage(0); }}
                        label="Quality Grade"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(QualityGrade).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{QualityGradeLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {tcaLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Symbol</TableCell>
                                        <TableCell sx={headCellSx}>Analysis Date</TableCell>
                                        <TableCell sx={headCellSx}>Avg Fill</TableCell>
                                        <TableCell sx={headCellSx}>Arrival Price</TableCell>
                                        <TableCell sx={headCellSx}>VWAP Benchmark</TableCell>
                                        <TableCell sx={headCellSx}>IS (bps)</TableCell>
                                        <TableCell sx={headCellSx}>Market Impact (bps)</TableCell>
                                        <TableCell sx={headCellSx}>All-In Cost (bps)</TableCell>
                                        <TableCell sx={headCellSx}>Quality Score</TableCell>
                                        <TableCell sx={headCellSx}>Grade</TableCell>
                                        <TableCell sx={headCellSx}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tcaRecords.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.Analytics sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No TCA records found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : tcaRecords.map((tca) => (
                                        <TableRow key={tca.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {tca.symbol || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                                {formatDate(tca.analysis_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatPrice(tca.avg_fill_price)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatPrice(tca.arrival_price)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {formatPrice(tca.vwap_benchmark)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {formatBps(tca.implementation_shortfall_bps)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {formatBps(tca.market_impact_bps)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                                                {formatBps(tca.all_in_cost_bps)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{
                                                    fontFamily,
                                                    fontWeight: 700,
                                                    color: getQualityScoreColor(tca.quality_score),
                                                    fontSize: '0.9rem',
                                                }}>
                                                    {tca.quality_score != null ? parseFloat(tca.quality_score).toFixed(0) : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={QualityGradeLabels[tca.quality_grade] || tca.quality_grade}
                                                    size="small"
                                                    color={QualityGradeColors[tca.quality_grade] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {canEdit && (
                                                    <Tooltip title="Run TCA">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRunTCA(tca.id)}
                                                            disabled={runningTcaId === tca.id}
                                                        >
                                                            {runningTcaId === tca.id ? (
                                                                <MuiIcons.HourglassEmpty sx={{ fontSize: 18, color: '#94a3b8' }} />
                                                            ) : (
                                                                <MuiIcons.PlayCircleOutline sx={{ fontSize: 18, color: ACCENT }} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={tcaTotal}
                            page={tcaPage}
                            onPageChange={(_, newPage) => setTcaPage(newPage)}
                            rowsPerPage={tcaPerPage}
                            onRowsPerPageChange={(e) => { setTcaPerPage(parseInt(e.target.value, 10)); setTcaPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: TAB 3 - TAX LOTS ====================
    const renderTaxLots = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Tax Lots
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        startIcon={<MuiIcons.FileDownload />}
                        onClick={() => exportToCSV(taxLots, 'tax_lots.csv')}
                        disabled={!taxLots.length}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        startIcon={<MuiIcons.Refresh />}
                        onClick={fetchTaxLots}
                        size="small"
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search symbol..."
                    value={taxSymbolFilter}
                    onChange={(e) => { setTaxSymbolFilter(e.target.value); setTaxPage(0); }}
                    sx={{ fontFamily, '& input': { fontFamily, fontSize: '0.85rem' } }}
                    InputProps={{
                        startAdornment: <MuiIcons.Search sx={{ fontSize: 18, color: '#94a3b8', mr: 0.5 }} />,
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontFamily }}>Lot Status</InputLabel>
                    <Select
                        value={taxStatusFilter}
                        onChange={(e) => { setTaxStatusFilter(e.target.value); setTaxPage(0); }}
                        label="Lot Status"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(TaxLotStatus).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{TaxLotStatusLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ fontFamily }}>Cost Basis Method</InputLabel>
                    <Select
                        value={taxMethodFilter}
                        onChange={(e) => { setTaxMethodFilter(e.target.value); setTaxPage(0); }}
                        label="Cost Basis Method"
                        sx={{ fontFamily, fontSize: '0.85rem' }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(CostBasisMethod).map(([key, val]) => (
                            <MenuItem key={key} value={val} sx={{ fontFamily }}>{CostBasisMethodLabels[val]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {taxLoading ? renderSkeleton() : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Symbol</TableCell>
                                        <TableCell sx={headCellSx}>Acquisition Date</TableCell>
                                        <TableCell sx={headCellSx}>Qty</TableCell>
                                        <TableCell sx={headCellSx}>Remaining</TableCell>
                                        <TableCell sx={headCellSx}>Cost Basis/Share</TableCell>
                                        <TableCell sx={headCellSx}>Total Basis</TableCell>
                                        <TableCell sx={headCellSx}>Status</TableCell>
                                        <TableCell sx={headCellSx}>Method</TableCell>
                                        <TableCell sx={headCellSx}>Gain/Loss</TableCell>
                                        <TableCell sx={headCellSx}>Term</TableCell>
                                        <TableCell sx={headCellSx}>Wash Sale</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {taxLots.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                                                <MuiIcons.AccountBalance sx={{ fontSize: 40, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                    No tax lots found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : taxLots.map((lot) => {
                                        const gainLoss = lot.realized_gain_loss != null ? parseFloat(lot.realized_gain_loss) : null;
                                        return (
                                            <TableRow key={lot.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {lot.symbol || '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                                    {formatDate(lot.acquisition_date)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {formatNumber(lot.quantity)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {formatNumber(lot.remaining_quantity)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {formatPrice(lot.cost_basis_per_share)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#1e293b', fontSize: '0.82rem' }}>
                                                    {formatPrice(lot.total_cost_basis)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={TaxLotStatusLabels[lot.lot_status] || lot.lot_status}
                                                        size="small"
                                                        color={TaxLotStatusColors[lot.lot_status] || 'default'}
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={CostBasisMethodLabels[lot.cost_basis_method] || lot.cost_basis_method}
                                                        size="small"
                                                        color={CostBasisMethodColors[lot.cost_basis_method] || 'default'}
                                                        variant="outlined"
                                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontFamily,
                                                    fontWeight: 700,
                                                    color: getGainLossColor(gainLoss),
                                                    fontSize: '0.82rem',
                                                }}>
                                                    {gainLoss != null ? `${gainLoss >= 0 ? '+' : ''}$${gainLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                                    {lot.holding_period || lot.term || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {lot.wash_sale ? (
                                                        <Tooltip title="Wash Sale Flagged">
                                                            <MuiIcons.Flag sx={{ fontSize: 20, color: '#ef4444' }} />
                                                        </Tooltip>
                                                    ) : (
                                                        <MuiIcons.FlagOutlined sx={{ fontSize: 20, color: '#cbd5e1' }} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={taxTotal}
                            page={taxPage}
                            onPageChange={(_, newPage) => setTaxPage(newPage)}
                            rowsPerPage={taxPerPage}
                            onRowsPerPageChange={(e) => { setTaxPerPage(parseInt(e.target.value, 10)); setTaxPage(0); }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            sx={{ fontFamily, borderTop: '1px solid #e2e8f0' }}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ==================== RENDER: RESOLVE BREAK DIALOG ====================
    const renderResolveDialog = () => (
        <Dialog open={resolveDialog} onClose={() => setResolveDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', borderBottom: '1px solid #e2e8f0', pb: 1.5 }}>
                Resolve Break
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {resolveTarget && (
                    <>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', mb: 1.5 }}>
                            Break Details
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Symbol</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{resolveTarget.symbol || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recon Date</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatDate(resolveTarget.recon_date)}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Internal Qty</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatNumber(resolveTarget.internal_quantity)}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Broker Qty</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{formatNumber(resolveTarget.broker_quantity)}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Break</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#ef4444', fontSize: '0.9rem' }}>{formatNumber(resolveTarget.break_quantity)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</Typography>
                                <Chip
                                    label={ReconStatusLabels[resolveTarget.recon_status] || resolveTarget.recon_status}
                                    size="small"
                                    color={ReconStatusColors[resolveTarget.recon_status] || 'default'}
                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', mt: 0.5 }}
                                />
                            </Grid>
                            {resolveTarget.break_type && (
                                <Grid item xs={6}>
                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Break Type</Typography>
                                    <Chip
                                        label={BreakTypeLabels[resolveTarget.break_type] || resolveTarget.break_type}
                                        size="small"
                                        color={BreakTypeColors[resolveTarget.break_type] || 'default'}
                                        variant="outlined"
                                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', mt: 0.5 }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Resolution Notes"
                            value={resolveNotes}
                            onChange={(e) => setResolveNotes(e.target.value)}
                            placeholder="Describe how the break was resolved..."
                            sx={{ fontFamily, '& textarea': { fontFamily, fontSize: '0.85rem' } }}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={() => setResolveDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleResolveBreak}
                    variant="contained"
                    disabled={submitting || !resolveNotes.trim()}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                >
                    {submitting ? 'Resolving...' : 'Resolve Break'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // ==================== MAIN RENDER ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ fontFamily }}>
                    You do not have permission to view post-trade processing. Required permission: <strong>tr-tee-post-trade</strong>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <MuiIcons.AssignmentTurnedIn sx={{ fontSize: 28, color: ACCENT }} />
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.4rem', lineHeight: 1.2 }}>
                        Post-Trade Processing
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.82rem' }}>
                        Trade confirmations, reconciliation, transaction cost analysis, and tax lot management
                    </Typography>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newVal) => setActiveTab(newVal)}
                    sx={{
                        px: 2,
                        '& .MuiTab-root': {
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            minHeight: 48,
                            color: '#64748b',
                            '&.Mui-selected': { color: ACCENT },
                        },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px 3px 0 0' },
                    }}
                >
                    <Tab icon={<MuiIcons.ReceiptLong sx={{ fontSize: 18 }} />} iconPosition="start" label="Trade Confirmations" />
                    <Tab icon={<MuiIcons.CompareArrows sx={{ fontSize: 18 }} />} iconPosition="start" label="Position Reconciliation" />
                    <Tab icon={<MuiIcons.Analytics sx={{ fontSize: 18 }} />} iconPosition="start" label="TCA" />
                    <Tab icon={<MuiIcons.AccountBalance sx={{ fontSize: 18 }} />} iconPosition="start" label="Tax Lots" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderConfirmations()}
            {activeTab === 1 && renderReconciliation()}
            {activeTab === 2 && renderTCA()}
            {activeTab === 3 && renderTaxLots()}

            {/* Dialogs */}
            {renderResolveDialog()}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    sx={{ fontFamily, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PostTradeProcessingPage;
